import React, {useState, useMemo, useRef} from 'react';
import { withStyles, Typography, Tooltip, IconButton } from '@material-ui/core';
import { connect } from 'react-redux';
import { Delete, FindInPage } from '@material-ui/icons'

import NotificationSettingList from './NotificationSettingList';
import * as actions from './store/notificationSettingActions';
import { Redirect } from 'react-router-dom';

import * as action from '../../np-core/Auth/store/authActions';
import { hasPermission, createHistory } from '../../sc-utilities';
import { getPossibleNotifications } from './utils';

const styles = theme => ({
    root: {
        width: `99.1%`,
        height: 'auto',
        margin: '7px',
        marginBottom: 0,
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const NotificationSetting = props => {
    const tableRef = useRef();
    const { classes, deleting, notificationSettings, onFetchNotificationSettingsCount, activeCustomer, translations, permissions, columns, saveColumnsFilter } = props;
    const fetching = notificationSettings.fetching;

    let visible = columns && columns['NotificationSetting'];
    const [deleteModalState, setDeleteModalState] = useState({ open: false, notificationSettingID: null, notificationSettingIDs: [], forBulk: false });
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, notificationSettingID: null, redirectPath: '' });
    const redirectHandler = (path, notificationSettingID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, notificationSettingID: notificationSettingID, redirectPath: path })
    };


    const handleDelete = async event => {
        if (!event) {
            setDeleteModalState({ open: false, notificationSettingID: null, notificationSettingIDs: [], forBulk: false });
            return;
        }
        const notificationSettingID = event.currentTarget.value;
        setDeleteModalState({ ...deleteModalState, open: true, notificationSettingID: notificationSettingID });
    };

    const handleBulkDelete = notificationSettingIDS => {
        setDeleteModalState({ ...deleteModalState, open: true, notificationSettingIDs: notificationSettingIDS, forBulk: true });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} notification settings`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(data => data.NotificationSettingID))
        }
    ]

    // All table actions listed
    const tableActionsRow = [
        {
            icon: 'FindInPage',
            tooltip: translations["edit_notification_setting"] || "Edit notification setting",
            onClick: (event, rowData) => redirectHandler(`/settings/notificationSetting/edit?notificationSettingID=${rowData.NotificationSettingID}`),
            hidden: !hasPermission(permissions, 'notification_setting_load')
        }
    ]

    const getIcon = icon => {
        switch (icon) {
            case 'FindInPage':
                return <FindInPage />
            default:
                return <FindInPage />
        }
    }

    const possibleNotifications = getPossibleNotifications(translations);

    /**
     * Write every column individually
     *    row data presenting current entry of table 
     */
    const tableColumns = useMemo(() => {
        return [{
            title: translations["action"] || "Action", field: 'NotificationSettingID',
            hidden: visible ? visible['NotificationSettingID'] : false,
            cellStyle: { minWidth: 50, maxWidth: 50 },
            filtering: false,
            render: rowData => {
                return tableActionsRow.map(action => {
                    if (action.hidden) {
                        return null;
                    }
                    return (
                        <Tooltip key={`${action.icon}-${rowData.NotificationSettingID}`} title={action.tooltip}>
                            <IconButton
                                component='div'
                                onClick={(event) => action.onClick(event, rowData)}
                            >
                                {
                                    getIcon(action.icon)
                                }
                            </IconButton>
                        </Tooltip>
                    )
                })
            },
        },
        {
            title: translations["customers"] || 'Customer', field: 'CustomerName',
            hidden: visible ? visible['CustomerName'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Customer && rowData.Customer.Name ? rowData.Customer.Name : `[${translations['system'] || 'System'}]`}</Typography>;
            }
        },
        {
            title: translations["receivers"] || 'Receivers', field: 'Receivers',
            hidden: visible ? visible['Receivers'] : false,
            render: rowData => {
                return <> {rowData.Receivers && rowData.Receivers.length > 30 ?
                    <Tooltip key={`${rowData.Receivers}-${rowData.NotificationSettingID}`} title={rowData.Receivers}>
                        <Typography variant={'caption'}>{`${rowData.Receivers.slice(0, 29)}...`}</Typography>
                    </Tooltip>
                    : <Typography variant={'caption'}>{rowData.Receivers ? rowData.Receivers : translations['short_not_assigned'] || 'n/a'}</Typography>}</>
            }
        },
        {
            title: translations["type"] || 'Type', field: 'Type',
            hidden: visible ? visible['Type'] : false,
            render: rowData => {
                let formattedTypes = rowData.Type.split(';').map(type => possibleNotifications[type]);
                formattedTypes = formattedTypes ? formattedTypes.join(',') : '';
                return <> {formattedTypes && formattedTypes.length > 30 ?
                    <Tooltip key={`${formattedTypes}-${rowData.NotificationSettingID}`} title={formattedTypes}>
                        <Typography variant={'caption'}>{`${formattedTypes.slice(0, 29)}...`}</Typography>
                    </Tooltip>
                    : <Typography variant={'caption'}>{formattedTypes || translations['short_not_assigned'] || 'n/a'}</Typography>}</>
            }
        }
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible])

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'notification_setting_list') ? <NotificationSettingList deleting={deleting} deleteModalState={deleteModalState} bulkDeleteNotificationSettings={props.onBulkDeleteNotificationSettings} tableActions={tableActions} saveColumnsFilter={saveColumnsFilter} fetching={fetching} tableColumns={tableColumns} translations={translations} activeCustomer={activeCustomer} columnsState={props.columns} permissions={permissions} handleBulkDelete={handleBulkDelete} notificationSettings={notificationSettings} onFetchNotificationSettingsCount={onFetchNotificationSettingsCount} onFetchNotificationSettings={props.onFetchNotificationSettings} handleDelete={handleDelete} tableRef={tableRef} /> : null}
        </div >
    );
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        notificationSettings: state.setting.notificationSettings,
        deleting: state.setting.notificationSettings.deleting,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        customers: state.core.login.customers,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchNotificationSettingsCount: filter => dispatch(actions.fetchNotificationSettingCount(filter)),
        onFetchNotificationSettings: filter => dispatch(actions.fetchNotificationSetting(filter)),
        onDeleteNotificationSetting: notificationSetting => dispatch(actions.deleteNotificationSetting(notificationSetting)),
        onBulkDeleteNotificationSettings: notificationSetting => dispatch(actions.bulkDeleteNotificationSettings(notificationSetting)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data))
    };
};
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(NotificationSetting));